/* The part of the context the XYZ charts derive from, and the operator that narrows to it. */

import { distinctUntilChanged, map, pipe, type OperatorFunction } from 'rxjs';
import { isEqual } from 'lodash';
import type { ContextShape } from '@ardoq/data-model';

/**
 * The only part of the context that can move a point.
 *
 * Narrow because `context$` rebuilds its whole shape on every `notifyContextChanged`,
 * `notifyWorkspaceChanged`, `notifyScenarioChanged`, `toggleExploreMode` and sort change - so
 * selecting a reference or reordering the workspaces rebuilds `points`, which the charts read as a
 * new dataset. `buildXyzPoints` reads exactly these two fields.
 */
export type PlottedContext = Pick<ContextShape, 'workspaceIds' | 'componentId'>;

const toPlottedContext = ({
  workspaceIds,
  componentId,
}: ContextShape): PlottedContext => ({ workspaceIds, componentId });

/**
 * Compared by content rather than by identity.
 *
 * `Context.workspaceIds()` answers with a `structuredClone`, so the array is new on every emission
 * and a comparison using `Object.is` lets each one through. Narrowed first so the comparison is
 * over the two fields the derivation reads and not over `sort`, `referenceId` and the rest -
 * `toContextShape` rebuilds `sort` as a fresh object every time, so a shallow compare of the whole
 * shape never matches.
 */
export const narrowToPlottedContext = (): OperatorFunction<
  ContextShape,
  PlottedContext
> => pipe(map(toPlottedContext), distinctUntilChanged(isEqual));
